import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User } from '@/types';
import { decodeAccessToken } from '@/utils/authClaims';

interface AuthState {
  token:        string | null;
  user:         User | null;
  clientIp:     string | null;
  setAuth:      (token: string, user: User, clientIp?: string) => void;
  setToken:     (token: string) => void;
  logout:       () => void;
  /** Reconcile the persisted user's role/clientOrgId from the JWT (source of truth). */
  reconcileFromToken: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token:        null,
      user:         null,
      clientIp:     null,
      setAuth: (token, user, clientIp) => set({ token, user, clientIp: clientIp ?? null }),
      setToken: (token) => set({ token }),
      logout: () => set({ token: null, user: null, clientIp: null }),
      reconcileFromToken: () =>
        set((state) => {
          if (!state.token || !state.user) return {};
          const claims = decodeAccessToken(state.token);
          if (!claims) return {};
          const nextClientOrgId = claims.clientOrgId ?? null;
          const nextRole = claims.role ?? state.user.role;
          if (state.user.clientOrgId === nextClientOrgId && state.user.role === nextRole) return {};
          // Keep the persisted profile in sync with the authoritative token so
          // any UI reading user.clientOrgId / user.role stays correct.
          return { user: { ...state.user, clientOrgId: nextClientOrgId, role: nextRole } };
        }),
    }),
    {
      name:    'grc-auth',
      // Bumping this version invalidates every persisted session on the next
      // load of a new bundle, forcing a one-time re-login so all users pick up
      // the latest auth/portal behaviour with a fresh token and profile.
      version: 1,
      migrate: () => ({ token: null, user: null, clientIp: null }),
      partialize: (state) => ({
        token:        state.token,
        user:         state.user,
        clientIp:     state.clientIp,
      }),
    },
  ),
);
